import Vue from 'vue';
import validate from 'mixins/validate';
import phone from 'mixins/phone-validation';
import signups from 'mixins/signups';
import { decode } from 'utils/general';
import 'lib/vue-phone-input.js';

const daysInMonths = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

export default (el) =>
  new Vue({
    components: {
      'text-input-alt': () => import('../text-input-alt/text-input-alt.vue'),
      checkbox: () => import('../checkbox/checkbox.vue'),
      'select-input': () => import('../select-input/select-input.vue'),
    },
    el,
    data: () => {
      return {
        ...Object.assign({}, el.dataset),
        generateDays: new Array(31)
          .fill(true)
          .map((_, index) => ({ label: index + 1, value: index + 1 })),
        birthdayRequired: false,
        birthday: [],
        country: '',
        acceptMarketing: false,
        phoneSendSMS: false,
        emailValue: '',
        formSent: false,
        isPhoneRequired: false,
        isEmailRequired: false,
      };
    },
    beforeMount() {
      this.months = JSON.parse(decode(this.months));
    },
    mounted() {},
    mixins: [validate, phone, signups],
    computed: {
      getPreheader() {
        if (this.formSent) {
          return this.submittedPreheader;
        }
        return this.initialPreheader;
      },
      getHeader() {
        if (this.formSent) {
          return this.submittedHeader;
        }
        return this.initialHeader;
      },
    },
    methods: {
      generateMonths() {
        return Object.keys(this.months).map((label) => ({
          label,
          value: this.months[label].numeric,
        }));
      },
      onPhoneChangeCallback(value) {
        const validPhone = this.phoneValidation(false, !!this.emailValue);
        if (validPhone) {
          this.validateCustomForm();
          this.isPhoneRequired = true;
        }
        if (!this.phoneValue) {
          this.isPhoneRequired = false;
        }
      },
      customValidateEmail({ value, name }) {
        if (!this.phoneValue && !value) {
          return false;
        }
        return this.validateEmail({ value, name });
      },
      onInputChange({ name, value }) {
        const match = /\[(\w*)\]/.exec(name);
        const daysDropdown = this.$refs['daysDropdown'];
        const monthsDropdown = this.$refs['monthsDropdown'];

        if (!match || !match[1]) {
          return false;
        }

        switch (match[1]) {
          case 'first_name':
            this.firstNameValue = value;
            break;
          case 'email': {
            this.emailValue = value;
            this.phoneValidation(true, !!value);
            this.isEmailRequired = !!value;
            return;
          }
          case 'country': {
            this.country = value;
            return;
          }
          case 'sms':
            this.phoneSendSMS = !!value;
            break;
          case 'accepts_marketing':
            this.acceptMarketing = !!value;
            break;
          case 'Month':
            monthsDropdown.isValid = true;
            if (!isNaN(+value)) {
              this.generateDays = new Array(daysInMonths[value - 1])
                .fill(true)
                .map((_, index) => ({ label: index + 1, value: index + 1 }));
            }
            this.birthday[0] = value;
            break;
          case 'Day':
            daysDropdown.isValid = true;
            this.birthday[1] = value;
            break;
          default:
            break;
        }

        this.phoneValidation(true);
      },
      submitForm() {
        const isValid = this.validateCustomForm();

        if (isValid) {
          this.subscribeUser();
        }
      },
      handleTracking() {
        dataLayer.push({
          event: 'offer_subscribe',
          offer_name: this.initialHeader,
          country: this.country,
          shopify_module_name: 'email signup form'
        });
      },
      validateCustomForm() {
        const daysDropdown = this.$refs['daysDropdown'];
        const monthsDropdown = this.$refs['monthsDropdown'];
        const countryDropdown = this.$refs['countryDropdown'];

        const checkBoxes = this.$children.filter(
          (c) =>
            typeof c.validateInput !== 'undefined' &&
            c.name.match(/\[(accepts_marketing|sms)\]/),
        );
        let isValid = this.$children
          .filter(
            (c) =>
              typeof c.validateInput !== 'undefined' &&
              !c.name.match(/\[(accepts_marketing|sms)\]/),
          )
          .map((c, index) => {
            const valid = c.validateInput();
            if (c.name.match(/\[email\]/)) {
              let validPhone = this.phoneValidation(true, false);
              if (!(valid && c.inputValue)) {
                // If its no email or its not valid check if user added a phone and if its valid
                validPhone = this.phoneValidation(true, false);
                this.highlightErrors(c, validPhone);
                return validPhone;
              } else {
                // If there is a valid email check is the phone number is valid but allow for blank
                validPhone = this.phoneValidation(true, true);
              }
              if (validPhone) {
                const phoneCheckbox = checkBoxes[1];
                this.highlightErrors(
                  phoneCheckbox,
                  phoneCheckbox.validateInput(),
                );
              }
              if (valid) {
                const mailCheckbox = checkBoxes[0];
                this.highlightErrors(
                  mailCheckbox,
                  mailCheckbox.validateInput(),
                );
              }
            }
            this.highlightErrors(c, valid);
            return valid;
          })
          .every((res) => res);

        // check if birthday is required
        if (this.birthdayRequired) {
          daysDropdown.isValid = daysDropdown.inputValue !== 'placeholder';
          monthsDropdown.isValid = monthsDropdown.inputValue !== 'placeholder';
          isValid = daysDropdown.isValid && monthsDropdown.isValid;
        }

        if (countryDropdown) {
          countryDropdown.isValid =
            countryDropdown.inputValue !== '' &&
            countryDropdown.inputValue !== 'placeholder';
          isValid = isValid && countryDropdown.isValid;
        }

        return isValid;
      },
      subscribeUser() {
        let sanitizeBirthday = '';
        if (this.birthday.every((val) => val !== 'placeholder')) {
          sanitizeBirthday = this.birthday.join('/');
        }
        const subscriptionPromises = [];
        if (this.emailValue) {
          subscriptionPromises.push(
            this.submitToKlaviyo({
              listId: this.klaviyoList,
              email: this.emailValue,
              $first_name: this.firstNameValue,
              $phone_number: this.phoneCountryValue + this.phoneValue,
              $country: this.country,
              acceptMarketing: this.acceptMarketing,
              birthday: sanitizeBirthday,
            }),
          );
        }
        if (this.phoneValue) {
          subscriptionPromises.push(this.submitToAttentive(this.attentiveId));
        }
        Promise.all(subscriptionPromises).then((responses) => {
          if (responses.every(Boolean)) {
            this.handleTracking();
            this.formSent = true;
            window.scroll({
              top: 0,
              left: 0,
              behavior: 'smooth',
            });
          } else {
            // TODO show some error or alert.
            console.error('Something went wrong on klaviyo or attentive');
          }
        });
      },
    },
  });
